import { z } from "zod"

import { documentTypeKeys, type DocumentTypeKey } from "../primitives.js"
import { exportTemplates, type DomainEventType } from "../taxonomy.js"
import {
  actionAppliesToDocumentType,
  exportTemplatesForDocumentType,
  fieldAppliesToDocumentType,
  selectableWorkflowTriggerValues,
  workflowActionEntry,
  workflowComparisonEntry,
  workflowFieldEntry,
  workflowValueOptions,
  type WorkflowActionCatalogEntry,
} from "./catalog.js"
import { detectWorkflowLoop } from "./loops.js"
import { isWorkflowAmountRange, toUtcDay } from "./values.js"
import {
  workflowComparisons,
  workflowConditionFields,
  workflowLimits,
  type WorkflowComparison,
  type WorkflowConditionField,
  type WorkflowValueKind,
} from "./vocabulary.js"

/* The shape of a saved rule, validated once here so the API, the engine and the
   client cannot disagree about what a rule is. The condition row stays a flat
   {field, comparison, value} triple and is checked against the field catalogue
   rather than against a second copy of the rules written in Zod, because the
   editor renders from that catalogue and the two drifting is exactly the bug
   this arrangement prevents. */

// --- conditions --------------------------------------------------------------

export const WorkflowAmountRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
})

export const WorkflowConditionValueSchema = z.union([
  z.string(),
  z.number(),
  WorkflowAmountRangeSchema,
])

export type WorkflowConditionValue = z.infer<
  typeof WorkflowConditionValueSchema
>

const currencyCodePattern = /^[A-Z]{3}$/
const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Two decimal places at most, so an amount a customer typed compares exactly,
 * and inside the range the money columns can hold. Negatives are allowed on
 * purpose: a statement's transaction total is usually one.
 */
function isStorableAmount(value: number): boolean {
  return (
    Number.isFinite(value) &&
    Math.abs(value) <= workflowLimits.maxAmount &&
    Math.abs(value * 100 - Math.round(value * 100)) < 1e-6
  )
}

type ValueIssue = { readonly message: string }

/** Kept distinct because each names the control the customer is looking at. */
const fixedListMessages = {
  flag_type: "choose a flag kind",
  reconciliation_status: "choose a reconciliation state",
  arrival_source: "choose how the document arrived",
} satisfies Partial<Record<WorkflowValueKind, string>>

const conditionStringSchema = z.string()
const conditionNumberSchema = z.number()

/**
 * Whether a stored value fits the control it was written in. Returns the
 * sentence to show rather than a boolean, because every one of these ends up in
 * front of a customer who needs to know which of eight rows is wrong.
 */
export function checkWorkflowConditionValue(
  valueKind: WorkflowValueKind,
  value: WorkflowConditionValue | undefined
): ValueIssue | null {
  if (valueKind === "none") {
    return value === undefined
      ? null
      : { message: "this comparison does not take a value" }
  }

  if (value === undefined) {
    return { message: "this comparison needs a value" }
  }

  switch (valueKind) {
    case "count": {
      if (
        !conditionNumberSchema.safeParse(value).success ||
        !Number.isInteger(conditionNumberSchema.parse(value)) ||
        conditionNumberSchema.parse(value) < 0 ||
        conditionNumberSchema.parse(value) > workflowLimits.maxCount
      ) {
        return {
          message: `enter a whole number between 0 and ${workflowLimits.maxCount}`,
        }
      }

      return null
    }
    case "amount": {
      const parsed = conditionNumberSchema.safeParse(value)
      if (!parsed.success || !isStorableAmount(parsed.data)) {
        return { message: "enter an amount with at most two decimal places" }
      }

      return null
    }
    case "amount_range": {
      if (!isWorkflowAmountRange(value)) {
        return { message: "enter both ends of the range" }
      }

      const range = value

      if (!isStorableAmount(range.min) || !isStorableAmount(range.max)) {
        return { message: "enter amounts with at most two decimal places" }
      }

      if (range.min > range.max) {
        return { message: "the lower amount has to come first" }
      }

      return null
    }
    case "text": {
      // The empty string is not a value, and this guard is load-bearing:
      // "anything".includes("") is true, so "who it is from contains <nothing>"
      // would be saveable and would match every document. A rule a customer
      // wrote to be narrow would post everything.
      if (
        !conditionStringSchema.safeParse(value).success ||
        conditionStringSchema.parse(value).trim().length === 0 ||
        conditionStringSchema.parse(value).length >
          workflowLimits.textValueMaxLength
      ) {
        return {
          message: `enter between 1 and ${workflowLimits.textValueMaxLength} characters`,
        }
      }

      return null
    }
    case "currency_code": {
      const parsed = conditionStringSchema.safeParse(value)
      if (!parsed.success || !currencyCodePattern.test(parsed.data)) {
        return { message: "enter a three letter currency code, such as USD" }
      }

      return null
    }
    case "days": {
      if (
        !conditionNumberSchema.safeParse(value).success ||
        !Number.isInteger(conditionNumberSchema.parse(value)) ||
        conditionNumberSchema.parse(value) < 1 ||
        conditionNumberSchema.parse(value) > workflowLimits.maxDaysWindow
      ) {
        return {
          message: `enter a whole number of days between 1 and ${workflowLimits.maxDaysWindow}`,
        }
      }

      return null
    }
    case "date": {
      const parsed = conditionStringSchema.safeParse(value)
      if (!parsed.success || toUtcDay(parsed.data) === null) {
        return { message: "enter a real date" }
      }

      return null
    }
    // One arm for the three fixed lists, reading the same map the editor
    // renders its options from. Three hand-written copies is how a picker and
    // its validator come to disagree about what is choosable.
    case "flag_type":
    case "reconciliation_status":
    case "arrival_source": {
      const options: readonly string[] = workflowValueOptions[valueKind]

      const parsed = conditionStringSchema.safeParse(value)
      if (!parsed.success || !options.includes(parsed.data)) {
        return { message: fixedListMessages[valueKind] }
      }

      return null
    }
    // Category choices are workspace data, not a compile-time list, so shape
    // is all this pure check can assert. A saved id whose category is later
    // deleted simply never matches, which fails closed.
    case "category": {
      const parsed = conditionStringSchema.safeParse(value)
      if (!parsed.success || !uuidPattern.test(parsed.data)) {
        return { message: "choose a category" }
      }

      return null
    }
    default: {
      const exhaustive: never = valueKind

      return { message: `unknown value kind ${String(exhaustive)}` }
    }
  }
}

/**
 * Tidies a value into the form the evaluator compares against, before it is
 * validated or stored. A customer typing "usd" or a name with a stray trailing
 * space means the same thing they would mean without it, and storing the tidy
 * version is what keeps the editor, the shorthand sentence and the comparison
 * showing the same string.
 */
function normalizeWorkflowConditionValue(condition: {
  field: WorkflowConditionField
  comparison: WorkflowComparison
  value?: WorkflowConditionValue
}) {
  const parsed = conditionStringSchema.safeParse(condition.value)
  if (!parsed.success) {
    return condition
  }

  const valueKind = workflowComparisonEntry(
    condition.field,
    condition.comparison
  )?.valueKind

  if (valueKind === "text") {
    return { ...condition, value: parsed.data.trim() }
  }

  if (valueKind === "currency_code") {
    return { ...condition, value: parsed.data.trim().toUpperCase() }
  }

  return condition
}

export const WorkflowConditionSchema = z
  .object({
    field: z.enum(workflowConditionFields),
    comparison: z.enum(workflowComparisons),
    value: WorkflowConditionValueSchema.optional(),
  })
  .transform(normalizeWorkflowConditionValue)
  .superRefine((condition, ctx) => {
    const comparison = workflowComparisonEntry(
      condition.field,
      condition.comparison
    )

    if (!comparison) {
      const field = workflowFieldEntry(condition.field)

      ctx.addIssue({
        code: "custom",
        path: ["comparison"],
        message: `${field?.label ?? condition.field} does not offer that comparison`,
      })

      return
    }

    const issue = checkWorkflowConditionValue(
      comparison.valueKind,
      condition.value
    )

    if (issue) {
      ctx.addIssue({ code: "custom", path: ["value"], message: issue.message })
    }
  })

export type WorkflowCondition = z.infer<typeof WorkflowConditionSchema>

// --- actions -----------------------------------------------------------------

export const WorkflowEmailActionSchema = z.object({
  actionType: z.literal("email_copy"),
  /* Every constraint below carries a written message, the way the refinements
     further down do. Without one, zod supplies its own ("Too small: expected
     array to have >=1 items"), and that string is what the API hands back as
     the reason a rule was refused. */
  recipients: z
    .array(
      z.string().email("That does not look like an email address.").max(320)
    )
    .min(1, "Add at least one address for this to go to.")
    .max(
      workflowLimits.maxEmailRecipients,
      `One email action can go to ${workflowLimits.maxEmailRecipients} addresses at most.`
    ),
  /** Mailing a client's source document out of LedgerBox is a deliberate act. */
  attachOriginal: z.boolean().default(false),
  /** Attaches the export an earlier action in the same run produced. */
  attachExport: z.boolean().default(false),
  note: z
    .string()
    .trim()
    .max(
      workflowLimits.emailNoteMaxLength,
      `Keep the note under ${workflowLimits.emailNoteMaxLength} characters.`
    )
    .optional(),
})

export const WorkflowActionSchema = z.discriminatedUnion("actionType", [
  z.object({ actionType: z.literal("post_to_quickbooks") }),
  WorkflowEmailActionSchema,
  z.object({
    actionType: z.literal("send_to_webhook"),
    webhookEndpointId: z.string().uuid("Choose which endpoint this sends to."),
  }),
  z.object({
    actionType: z.literal("build_export"),
    template: z.enum(exportTemplates, "Choose which template to build."),
  }),
])

export type WorkflowAction = z.infer<typeof WorkflowActionSchema>

export type WorkflowEmailAction = z.infer<typeof WorkflowEmailActionSchema>

export function isEmailAction(
  action: WorkflowAction
): action is WorkflowEmailAction {
  return action.actionType === "email_copy"
}

/**
 * Ordering the editor warns about rather than forbids: attaching an export to
 * an email only works when the export was built earlier in the same run.
 */
export function workflowOrderingWarnings(
  actions: readonly WorkflowAction[]
): readonly string[] {
  const warnings: string[] = []
  const exportIndex = actions.findIndex(
    (action) => action.actionType === "build_export"
  )

  actions.forEach((action, index) => {
    if (!isEmailAction(action) || !action.attachExport) {
      return
    }

    if (exportIndex === -1) {
      warnings.push(
        `Step ${index + 1} attaches an export, but nothing in this workflow builds one.`
      )

      return
    }

    if (exportIndex > index) {
      warnings.push(
        `Step ${index + 1} attaches an export that step ${exportIndex + 1} builds afterwards. Move the export above it.`
      )
    }
  })

  return warnings
}

// --- the whole rule ----------------------------------------------------------

export const WorkflowDefinitionSchema = z
  .object({
    // The message on the type covers a name that is missing or not a string;
    // the one on .min covers a name that is there and empty.
    name: z
      .string("Give this workflow a name.")
      .trim()
      .min(1, "Give this workflow a name.")
      .max(
        workflowLimits.nameMaxLength,
        `Keep the name under ${workflowLimits.nameMaxLength} characters.`
      ),
    triggerEventType: z.enum(
      selectableWorkflowTriggerValues,
      "Choose what starts this workflow."
    ),
    /** Null means every document type. */
    documentTypeKey: z
      .enum(documentTypeKeys, "Choose which kind of document this is about.")
      .nullable(),
    conditions: z
      .array(WorkflowConditionSchema, "Checks are a list, even an empty one.")
      .max(
        workflowLimits.maxConditions,
        `${workflowLimits.maxConditions} checks is the most one workflow can have.`
      ),
    actions: z
      .array(
        WorkflowActionSchema,
        "Add at least one action, or this workflow has nothing to do."
      )
      .min(1, "Add at least one action, or this workflow has nothing to do.")
      .max(
        workflowLimits.maxActions,
        `${workflowLimits.maxActions} actions is the most one workflow can have.`
      ),
  })
  .superRefine((definition, ctx) => {
    definition.conditions.forEach((condition, index) => {
      const field = workflowFieldEntry(condition.field)

      if (
        field &&
        !fieldAppliesToDocumentType(field, definition.documentTypeKey)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["conditions", index, "field"],
          message: `${field.label} does not apply to this trigger`,
        })
      }
    })

    const loop = detectWorkflowLoop(
      definition.triggerEventType,
      definition.actions
    )

    if (loop) {
      ctx.addIssue({
        code: "custom",
        path: ["actions"],
        message:
          "This workflow would trigger itself. Change the trigger or remove the action that produces the same event.",
      })
    }

    // Posting twice in one rule cannot post twice, because the posting is
    // idempotent per document, so the second step is a step that can only fail.
    const postingSteps = definition.actions.filter(
      (action) => action.actionType === "post_to_quickbooks"
    )

    if (postingSteps.length > 1) {
      ctx.addIssue({
        code: "custom",
        path: ["actions"],
        message: "A workflow can post to QuickBooks once.",
      })
    }

    // A rule scoped to one type can be held to that type's capabilities. A rule
    // covering every type cannot: "post every approved document" is a
    // reasonable thing to write, and the run records a clear reason on a
    // document the action cannot handle. So this only fires when the customer
    // has already narrowed the rule, where the mistake is avoidable.
    if (definition.documentTypeKey !== null) {
      const documentTypeKey = definition.documentTypeKey
      const templates = exportTemplatesForDocumentType(documentTypeKey)

      definition.actions.forEach((action, index) => {
        const entry = workflowActionEntry(action.actionType)

        if (entry && !actionAppliesToDocumentType(entry, documentTypeKey)) {
          ctx.addIssue({
            code: "custom",
            path: ["actions", index, "actionType"],
            message: `${entry.label} does not work on this document type`,
          })
        }

        if (
          action.actionType === "build_export" &&
          !templates.includes(action.template)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["actions", index, "template"],
            message:
              "That export template does not apply to this document type",
          })
        }
      })
    }
  })

export type WorkflowDefinition = z.infer<typeof WorkflowDefinitionSchema>

export type StoredWorkflowDefinition = {
  readonly conditions: readonly WorkflowCondition[]
  readonly actions: readonly WorkflowAction[]
}

/**
 * The checks and actions of a rule that is already in the database.
 *
 * A rule is validated when it is saved and never again, so a field that was
 * retired or an export template that was removed leaves a row that no longer
 * parses. Null rather than a throw: the engine reads these in a batch, and one
 * unreadable rule must not take the rules beside it down with it.
 *
 * All or nothing on purpose. Keeping the checks that still parse and dropping
 * the one that does not would widen the rule, and a rule that matches more
 * documents than its author wrote is the one failure mode that posts into a
 * customer's books.
 */
export function readStoredWorkflowDefinition(stored: {
  readonly conditions: unknown
  readonly actions: unknown
}): StoredWorkflowDefinition | null {
  const conditions = WorkflowConditionSchema.array().safeParse(stored.conditions)
  const actions = WorkflowActionSchema.array().safeParse(stored.actions)

  if (!conditions.success || !actions.success) {
    return null
  }

  return { conditions: conditions.data, actions: actions.data }
}

/**
 * Whether turning this rule on has to be confirmed, and what the confirmation
 * has to name. Anything that leaves LedgerBox does; building a file inside it
 * does not.
 */
export function workflowActionsLeavingLedgerBox(
  actions: readonly WorkflowAction[]
): readonly WorkflowActionCatalogEntry[] {
  const entries: WorkflowActionCatalogEntry[] = []

  for (const action of actions) {
    const entry = workflowActionEntry(action.actionType)

    if (entry?.leavesLedgerBox && !entries.includes(entry)) {
      entries.push(entry)
    }
  }

  return entries
}

/**
 * Triggers that can fire before a person has looked at the document. A rule
 * that posts from one of these says so in its confirmation, once, in one
 * sentence.
 *
 * `export_generated` belongs here even though an export often does follow an
 * approval: nothing requires one. `createDocumentExport` has no approval gate,
 * and the export row records `isApproved: false` when there is no matching
 * approval, so "an export is ready, post it" can write into a customer's books
 * from figures nobody checked. The confirmation is the only thing standing
 * there, so it errs towards being shown.
 */
export const preApprovalTriggerEventTypes = [
  "document_processed",
  "flags_created",
  "batch_completed",
  "export_generated",
] as const satisfies readonly DomainEventType[]
const preApprovalTriggerEventTypeSet = new Set<string>(
  preApprovalTriggerEventTypes
)

export function postsBeforeAnyoneReviewed(definition: {
  readonly triggerEventType: string
  readonly actions: readonly { readonly actionType: string }[]
}): boolean {
  return (
    preApprovalTriggerEventTypeSet.has(definition.triggerEventType) &&
    definition.actions.some(
      (action) => action.actionType === "post_to_quickbooks"
    )
  )
}

/**
 * Which saved checks a trigger change would drop, so the editor can say which
 * ones and why before the customer saves rather than after.
 */
export function conditionsDroppedByDocumentType(
  conditions: readonly WorkflowCondition[],
  documentTypeKey: DocumentTypeKey | null
): readonly WorkflowCondition[] {
  return conditions.filter((condition) => {
    const field = workflowFieldEntry(condition.field)

    return (
      field !== undefined && !fieldAppliesToDocumentType(field, documentTypeKey)
    )
  })
}
